import { redirect } from "@tanstack/react-router";
import { queryClient, trpc } from "@/trpc/client";
import { getUserWorkspaces } from "@/lib/workspaces";
import { showsUpdatesStep } from "@/lib/onboardingFlow";

/**
 * Fresh workspaces start with a purpose question. Email users finish there;
 * publishers continue through address, design, and first-post setup.
 *
 * `titleIsCustom` keeps existing publications out of first-time onboarding,
 * even if their settings predate `onboardingSeen`. The persisted flag makes
 * completion one-time for email users and publishers who skip every step.
 * It is written on exit, so abandoning setup does not silently finish it.
 *
 * Read failures fail open: onboarding must never block dashboard access
 * because a blog or settings lookup is unavailable.
 */
export async function guardFreshWorkspaceOnboarding({
  context,
  params,
}: {
  context: { auth: { isLoading: boolean } };
  params: { workspaceId: string };
}): Promise<void> {
  if (context.auth.isLoading) return;

  let titleIsCustom: boolean;
  try {
    const blog = await queryClient.ensureQueryData(
      trpc.authenticatedRouterFunction.getBlog.queryOptions({
        workspaceId: params.workspaceId,
      }),
    );
    titleIsCustom = blog.titleIsCustom;
  } catch {
    return;
  }
  if (titleIsCustom) return;

  let onboardingSeen: boolean;
  try {
    const settings = await queryClient.ensureQueryData(
      trpc.authenticatedRouterFunction.getWorkspaceSettings.queryOptions({
        workspaceId: params.workspaceId,
      }),
    );
    onboardingSeen = settings.onboardingSeen;
  } catch {
    return;
  }
  if (onboardingSeen) return;

  throw redirect({
    to: "/onboarding/$workspaceId/purpose",
    params: { workspaceId: params.workspaceId },
  });
}

/**
 * The `beforeLoad` of `/onboarding/$workspaceId/updates` (screen 3, spec
 * 1136): that screen offers the account-level "Updates from GoodSender"
 * consent, made once at registration, so it applies only to the caller's
 * REGISTRATION workspace — `showsUpdatesStep` decides which one that is.
 * Direct entry (typed URL, bookmark, browser Back) to any other workspace's
 * `/updates` redirects straight to `/first-post` instead of rendering an
 * offer that has nothing to do with the workspace just created.
 *
 * Reads `getUserWorkspaces()` (never a direct `fetchQuery`) — the same
 * signup-provisioning-barrier rule `guardWorkspaceAccess` follows — which by
 * the time this guard runs (always composed AFTER `guardWorkspaceAccess` on
 * this route) is a cache hit. A rejected read fails OPEN: it returns without
 * redirecting, so an inconclusive signal shows today's step rather than
 * silently skipping it (spec 1136 A3, S4).
 */
export async function guardUpdatesStep({
  context,
  params,
}: {
  context: { auth: { isLoading: boolean } };
  params: { workspaceId: string };
}): Promise<void> {
  if (context.auth.isLoading) return;

  let workspaces: Awaited<ReturnType<typeof getUserWorkspaces>>;
  try {
    workspaces = await getUserWorkspaces();
  } catch {
    return;
  }
  if (showsUpdatesStep(workspaces, params.workspaceId)) return;

  throw redirect({
    to: "/onboarding/$workspaceId/first-post",
    params: { workspaceId: params.workspaceId },
  });
}
